import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Plus } from 'lucide-react'
import type { SaveSonarQubeConnectionRequest, SonarQubeConnection, TestSonarQubeConnectionRequest } from '@/types'
import {
  CONNECTOR_DESCRIPTIONS,
  CONNECTOR_HOW_IT_WORKS,
  CONNECTOR_LABELS,
  CONNECTOR_OVERVIEWS,
} from './connectorConstants'
import { CONNECTOR_ICONS } from './connectorIcons'
import { SonarQubeConnectionForm } from './SonarQubeConnectionForm'

interface SonarQubeConnectorPanelProps {
  connection: SonarQubeConnection | undefined
  open: boolean
  onOpenChange: (open: boolean) => void
  onTest: (data: TestSonarQubeConnectionRequest) => Promise<void>
  onSave: (data: SaveSonarQubeConnectionRequest) => Promise<void>
  onDisableRequested: () => void
  onEnableRequested: () => void
  isTesting: boolean
  isSaving: boolean
  canManage: boolean
  tokenInlineError?: string | null
  saveError?: string | null
}

export function SonarQubeConnectorPanel({
  connection,
  open,
  onOpenChange,
  onTest,
  onSave,
  onDisableRequested,
  onEnableRequested,
  isTesting,
  isSaving,
  canManage,
  tokenInlineError,
  saveError,
}: SonarQubeConnectorPanelProps) {
  const isConnected = connection !== undefined
  const Icon = CONNECTOR_ICONS.SonarQube

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-none w-screen h-screen m-0 rounded-none data-[state=open]:slide-in-from-bottom-0 data-[state=closed]:slide-out-to-bottom-0 left-0 top-0 translate-x-0 translate-y-0 flex flex-col p-0 gap-0 border-0"
      >
        <DialogHeader className="flex-none border-b px-6 py-4">
          <DialogTitle>{isConnected ? CONNECTOR_LABELS.SonarQube : 'Enable Connector'}</DialogTitle>
        </DialogHeader>

        <ScrollArea className="flex-1">
          <div className="max-w-3xl mx-auto px-6 py-8 space-y-8">
            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-muted shrink-0">
                <Icon className="h-7 w-7" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <h2 className="text-xl font-semibold">{CONNECTOR_LABELS.SonarQube}</h2>
                  {isConnected && (
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full border bg-success text-success-foreground border-success-foreground" data-testid="connector-connected-status">
                      Connected
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">{CONNECTOR_DESCRIPTIONS.SonarQube}</p>
                {canManage && isConnected && (
                  <div className="mt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onDisableRequested}
                      className="text-destructive hover:text-destructive"
                      data-testid="disconnect-connector-button"
                    >
                      Disable
                    </Button>
                  </div>
                )}
                {canManage && !isConnected && (
                  <div className="mt-3">
                    <Button size="sm" onClick={onEnableRequested} data-testid="enable-connector-button">
                      <Plus className="h-4 w-4 mr-1" />
                      Enable
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <Separator />

            <div className="space-y-3">
              <h3 className="text-lg font-semibold">Overview</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{CONNECTOR_OVERVIEWS.SonarQube}</p>
            </div>

            <div className="space-y-3">
              <h3 className="text-lg font-semibold">How It Works</h3>
              <ol className="list-decimal list-inside space-y-2">
                {CONNECTOR_HOW_IT_WORKS.SonarQube.map((step, index) => (
                  <li key={index} className="text-sm text-muted-foreground leading-relaxed">{step}</li>
                ))}
              </ol>
            </div>

            {canManage && isConnected && (
              <>
                <Separator />
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold">Connection Settings</h3>
                  <p className="text-sm text-muted-foreground">Enter the SonarQube details needed to connect your project.</p>
                  <SonarQubeConnectionForm
                    connection={connection}
                    open={open}
                    onTest={onTest}
                    onSave={onSave}
                    isTesting={isTesting}
                    isSaving={isSaving}
                    tokenInlineError={tokenInlineError}
                    saveError={saveError}
                  />
                </div>
              </>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}
